import { useState } from "react";

import * as api from "../api/client";
import { AcquisitionFunnel } from "../components/AcquisitionFunnel";
import { DailyMetricsFreshness } from "../components/DailyMetricsFreshness";
import { FirstPaymentCohorts } from "../components/FirstPaymentCohorts";
import { PerRequestStats } from "../components/PerRequestStats";
import { QuotaExceededHits } from "../components/QuotaExceededHits";
import { RecordingCapHits } from "../components/RecordingCapHits";
import { RecordingDurationDistribution } from "../components/RecordingDurationDistribution";
import { SuccessRateByVersion } from "../components/SuccessRateByVersion";
import { TimeSeriesChart } from "../components/TimeSeriesChart";
import { TopExpensiveTable } from "../components/TopExpensiveTable";
import { UnitEconomics } from "../components/UnitEconomics";
import { useApi } from "../hooks/useApi";

export function AnalyticsPage() {
  const [days, setDays] = useState(30);
  const [recordingDurationDays, setRecordingDurationDays] = useState(1);
  const [recordingCapHitsDays, setRecordingCapHitsDays] = useState(1);
  const [quotaExceededHitsDays, setQuotaExceededHitsDays] = useState(1);
  const [successRateDays, setSuccessRateDays] = useState(1);
  const [unitEconomicsDays, setUnitEconomicsDays] = useState(1);

  const timeseries = useApi(() => api.getTimeseries(days), [days]);
  const perRequest = useApi(() => api.getPerRequestStats(days, 20), [days]);
  const recordingDuration = useApi(
    () => api.getRecordingDurationStats(recordingDurationDays),
    [recordingDurationDays]
  );
  const recordingCapHits = useApi(
    () => api.getRecordingCapHits(recordingCapHitsDays),
    [recordingCapHitsDays]
  );
  // V2M-DEV-012 sygnał #1: `quota_exceeded` docierał WYŁĄCZNIE do Plausible
  // (agregat, bez modelu per-użytkownik) — zero endpointu admina, zero
  // konsumenta panelu, grep po `quota-exceeded` w tym pliku dawał zero
  // trafień przed tą zmianą.
  const quotaExceededHits = useApi(
    () => api.getQuotaExceededHits(quotaExceededHitsDays),
    [quotaExceededHitsDays]
  );
  // V2M-DEV-012 sygnał #12 (druga połowa): endpoint istniał od commitu
  // 94ea921c z zerem konsumentów w panelu — grep po `successRate` w całym
  // `src/` nie dawał ani jednego trafienia.
  const successRateByVersion = useApi(
    () => api.getSuccessRateByVersion(successRateDays),
    [successRateDays]
  );
  // V2M-DEV-163: three endpoints below existed with zero panel consumers —
  // grep in src/api/client.ts found not even wrapper functions.
  const dailyMetricsFreshness = useApi(() => api.getDailyMetricsFreshness(), []);
  const funnel = useApi(() => api.getFunnelMetrics(), []);
  const firstPaymentCohorts = useApi(() => api.getFirstPaymentCohorts(20), []);
  const unitEconomics = useApi(
    () => api.getUnitEconomics(unitEconomicsDays, 20),
    [unitEconomicsDays]
  );

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 mb-1">Analytics</h2>
        <p className="text-sm text-slate-600">
          Daily usage trends and per-request anomaly detection
        </p>
      </div>

      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-slate-900">Świeżość warstwy pomiarowej</h3>
        <p className="text-sm text-slate-600">
          Czy <code>daily_metrics</code> w ogóle jest dziś zapisywane (V2M-DEV-080) — ten sam
          sprawdzian co zaplanowany watchdog, na żywo i tylko do odczytu.
        </p>
        {dailyMetricsFreshness.loading && (
          <div className="text-slate-500 text-sm">Loading daily-metrics-freshness...</div>
        )}
        {dailyMetricsFreshness.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
            <div className="text-red-800 text-sm font-medium">
              Error: {dailyMetricsFreshness.error}
            </div>
          </div>
        )}
        {dailyMetricsFreshness.data && <DailyMetricsFreshness data={dailyMetricsFreshness.data} />}
      </section>

      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-slate-900">Daily Trends</h3>
        {timeseries.loading && <div className="text-slate-500 text-sm">Loading time-series...</div>}
        {timeseries.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
            <div className="text-red-800 text-sm font-medium">Error: {timeseries.error}</div>
          </div>
        )}
        {timeseries.data && (
          <TimeSeriesChart buckets={timeseries.data.buckets} days={days} onDaysChange={setDays} />
        )}
      </section>

      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-slate-900">Per-Request Stats</h3>
        {perRequest.loading && <div className="text-slate-500 text-sm">Loading stats...</div>}
        {perRequest.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
            <div className="text-red-800 text-sm font-medium">Error: {perRequest.error}</div>
          </div>
        )}
        {perRequest.data && <PerRequestStats stats={perRequest.data} />}
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-lg font-semibold text-slate-900">Recording Length Distribution</h3>
          <div className="flex gap-1">
            {[1, 7, 30].map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRecordingDurationDays(r)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  recordingDurationDays === r
                    ? "bg-blue-600 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {r}d
              </button>
            ))}
          </div>
        </div>
        <p className="text-sm text-slate-600">
          Percentyle długości nagrania (p50/p90/p95/p99/max), rozbite na limit Free (180s) i płatny
          (300s) — decyduje o reaktywacji mnożnika kredytowego (checkpoint
          2026-07-25-v2m-projekt-metryk-12-sygnalow.md, M-B).
        </p>
        {recordingDuration.loading && (
          <div className="text-slate-500 text-sm">Loading recording-length distribution...</div>
        )}
        {recordingDuration.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
            <div className="text-red-800 text-sm font-medium">Error: {recordingDuration.error}</div>
          </div>
        )}
        {recordingDuration.data && <RecordingDurationDistribution data={recordingDuration.data} />}
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-lg font-semibold text-slate-900">Dobicia do limitu nagrania</h3>
          <div className="flex gap-1">
            {[1, 7, 30].map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setRecordingCapHitsDays(r)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  recordingCapHitsDays === r
                    ? "bg-blue-600 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {r}d
              </button>
            ))}
          </div>
        </div>
        <p className="text-sm text-slate-600">
          Licznik dobić do twardego limitu (M-C, checkpoint
          2026-07-25-v2m-projekt-metryk-12-sygnalow.md) — ile razy nagranie zatrzymał limit czasu,
          nie użytkownik.
        </p>
        {recordingCapHits.loading && (
          <div className="text-slate-500 text-sm">Loading recording-cap-hits...</div>
        )}
        {recordingCapHits.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
            <div className="text-red-800 text-sm font-medium">Error: {recordingCapHits.error}</div>
          </div>
        )}
        {recordingCapHits.data && <RecordingCapHits data={recordingCapHits.data} />}
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-lg font-semibold text-slate-900">Uderzenia w limit generacji</h3>
          <div className="flex gap-1">
            {[1, 7, 30].map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setQuotaExceededHitsDays(r)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  quotaExceededHitsDays === r
                    ? "bg-blue-600 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {r}d
              </button>
            ))}
          </div>
        </div>
        <p className="text-sm text-slate-600">
          Sygnał #1 (checkpoint 2026-07-25-v2m-projekt-metryk-12-sygnalow.md) — czy nie sprzedajemy,
          bo nie chcą płacić, czy bo nigdy nie docierają do limitu. Dwie przeciwne interwencje.
        </p>
        {quotaExceededHits.loading && (
          <div className="text-slate-500 text-sm">Loading quota-exceeded-hits...</div>
        )}
        {quotaExceededHits.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
            <div className="text-red-800 text-sm font-medium">Error: {quotaExceededHits.error}</div>
          </div>
        )}
        {quotaExceededHits.data && <QuotaExceededHits data={quotaExceededHits.data} />}
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-lg font-semibold text-slate-900">
            Skuteczność generacji wg wersji wtyczki
          </h3>
          <div className="flex gap-1">
            {[1, 7, 30].map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setSuccessRateDays(r)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  successRateDays === r
                    ? "bg-blue-600 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {r}d
              </button>
            ))}
          </div>
        </div>
        <p className="text-sm text-slate-600">
          Sygnał #12 druga połowa (checkpoint 2026-07-25-v2m-projekt-metryk-12-sygnalow.md) —
          regresja wprowadzona nową wersją wtyczki rozpuszcza się w globalnej średniej dokładnie
          wtedy, gdy jest najgroźniejsza: tuż po wypuszczeniu.
        </p>
        {successRateByVersion.loading && (
          <div className="text-slate-500 text-sm">Loading success-rate-by-version...</div>
        )}
        {successRateByVersion.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
            <div className="text-red-800 text-sm font-medium">
              Error: {successRateByVersion.error}
            </div>
          </div>
        )}
        {successRateByVersion.data && <SuccessRateByVersion data={successRateByVersion.data} />}
      </section>

      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-slate-900">Lejek aktywacji i konwersji</h3>
        <p className="text-sm text-slate-600">
          E4 (instalacja → pierwsza generacja) i E5 (pierwsza generacja → płatność), checkpoint
          2026-07-29-lejek-akwizycji-definicja-i-metryki.md. Liczone na żywo, all-time, wprost z
          kolekcji <code>users</code>/<code>generations</code>/<code>revenue_ledger</code> — bez
          okna dni.
        </p>
        {funnel.loading && <div className="text-slate-500 text-sm">Loading funnel...</div>}
        {funnel.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
            <div className="text-red-800 text-sm font-medium">Error: {funnel.error}</div>
          </div>
        )}
        {funnel.data && <AcquisitionFunnel data={funnel.data} />}
      </section>

      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-slate-900">Kohorta pierwszej płatności</h3>
        <p className="text-sm text-slate-600">
          Sygnał #5 (V2M-DEV-012, checkpoint 2026-07-25-v2m-projekt-metryk-12-sygnalow.md) —
          użytkownicy pogrupowani wg miesiąca ich PIERWSZEJ płatności (subskrypcja lub pakiet
          jednorazowy), all-time, bez okna dni. Przy dzisiejszej niemal zerowej bazie płacących
          pusta tabela jest poprawnym wynikiem — patrz rozróżnienie zmierzone-zero/nieudane-zapisy
          poniżej.
        </p>
        {firstPaymentCohorts.loading && (
          <div className="text-slate-500 text-sm">Loading first-payment-cohorts...</div>
        )}
        {firstPaymentCohorts.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
            <div className="text-red-800 text-sm font-medium">
              Error: {firstPaymentCohorts.error}
            </div>
          </div>
        )}
        {firstPaymentCohorts.data && <FirstPaymentCohorts data={firstPaymentCohorts.data} />}
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-lg font-semibold text-slate-900">Unit economics</h3>
          <div className="flex gap-1">
            {[1, 7, 30].map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setUnitEconomicsDays(r)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  unitEconomicsDays === r
                    ? "bg-blue-600 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {r}d
              </button>
            ))}
          </div>
        </div>
        <p className="text-sm text-slate-600">
          Koszt tokenowy per klient i wypalanie darmowego poziomu (PK, dyrektywa 2026-07-24).
        </p>
        {unitEconomics.loading && (
          <div className="text-slate-500 text-sm">Loading unit-economics...</div>
        )}
        {unitEconomics.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4" role="alert">
            <div className="text-red-800 text-sm font-medium">Error: {unitEconomics.error}</div>
          </div>
        )}
        {unitEconomics.data && <UnitEconomics data={unitEconomics.data} />}
      </section>

      <section className="space-y-3">
        <h3 className="text-lg font-semibold text-slate-900">Anomalies — Most Expensive</h3>
        <p className="text-sm text-slate-600">
          Highest-cost generations. A &ldquo;long context&rdquo; badge flags users above the P95
          context length.
        </p>
        {perRequest.data && (
          <TopExpensiveTable
            rows={perRequest.data.topExpensive}
            contextP95={perRequest.data.contextLength.p95}
          />
        )}
      </section>
    </div>
  );
}
